import React from "react"
import {
  CalendarDays,
  Users2,
  Cpu,
  Workflow,
  Link as LinkIcon,
  Gauge,
  Landmark,
  ShieldCheck,
  FileCheck,
  Lock,
  BarChart3,
  Stethoscope,
  Syringe,
  Building,
  GraduationCap,
  Building2,
  Clock,
  CalendarCheck,
} from "lucide-react"
import { useNavigationStore } from "@/stores/navigation"
import { useTranslation } from "react-i18next"
import CustomerLogos from "@/components/common/CustomerLogos"

const SchedulingCX: React.FC = () => {
  const navigationStore = useNavigationStore()
  const { t } = useTranslation()

  const features = [
    {
      title: t("schedulingcx.features.items.enterpriseScheduling.title"),
      body: t("schedulingcx.features.items.enterpriseScheduling.body"),
      Icon: Landmark,
    },
    {
      title: t("schedulingcx.features.items.selfSchedulingPortal.title"),
      body: t("schedulingcx.features.items.selfSchedulingPortal.body"),
      Icon: Users2,
    },
    {
      title: t("schedulingcx.features.items.multiResourceLogic.title"),
      body: t("schedulingcx.features.items.multiResourceLogic.body"),
      Icon: Workflow,
    },
    {
      title: t("schedulingcx.features.items.singleOrRecurring.title"),
      body: t("schedulingcx.features.items.singleOrRecurring.body"),
      Icon: CalendarCheck,
    },
    {
      title: t("schedulingcx.features.items.virtualAndInPerson.title"),
      body: t("schedulingcx.features.items.virtualAndInPerson.body"),
      Icon: CalendarDays,
    },
    {
      title: t("schedulingcx.features.items.ehrIntegration.title"),
      body: t("schedulingcx.features.items.ehrIntegration.body"),
      Icon: LinkIcon,
    },
  ]

  const useCases = [
    {
      title: t("schedulingcx.useCases.items.clinicOutpatient.title"),
      body: t("schedulingcx.useCases.items.clinicOutpatient.body"),
      Icon: Stethoscope,
    },
    {
      title: t("schedulingcx.useCases.items.primarySpecialty.title"),
      body: t("schedulingcx.useCases.items.primarySpecialty.body"),
      Icon: Gauge,
    },
    {
      title: t("schedulingcx.useCases.items.iepSchoolBasedCare.title"),
      body: t("schedulingcx.useCases.items.iepSchoolBasedCare.body"),
      Icon: GraduationCap,
    },
    {
      title: t("schedulingcx.useCases.items.cancerTreatment.title"),
      body: t("schedulingcx.useCases.items.cancerTreatment.body"),
      Icon: Building,
    },
    {
      title: t("schedulingcx.useCases.items.publicHealthVaccination.title"),
      body: t("schedulingcx.useCases.items.publicHealthVaccination.body"),
      Icon: Syringe,
    },
    {
      title: t("schedulingcx.useCases.items.occupationalHealth.title"),
      body: t("schedulingcx.useCases.items.occupationalHealth.body"),
      Icon: Building2,
    },
    {
      title: t("schedulingcx.useCases.items.conciergeMedicine.title"),
      body: t("schedulingcx.useCases.items.conciergeMedicine.body"),
      Icon: Clock,
    },
    {
      title: t("schedulingcx.useCases.items.cellularTherapy.title"),
      body: t("schedulingcx.useCases.items.cellularTherapy.body"),
      Icon: Cpu,
    },
  ]

  const whyBullets = [
    {
      title: t(
        "schedulingcx.whySchedulingcx.items.simplifiedIntelligentScheduling.title"
      ),
      details: t(
        "schedulingcx.whySchedulingcx.items.simplifiedIntelligentScheduling.details",
        { returnObjects: true }
      ) as string[],
      Icon: Workflow,
    },
    {
      title: t(
        "schedulingcx.whySchedulingcx.items.fewerNoShowsHappierPatients.title"
      ),
      details: t(
        "schedulingcx.whySchedulingcx.items.fewerNoShowsHappierPatients.details",
        { returnObjects: true }
      ) as string[],
      Icon: CalendarDays,
    },
    {
      title: t(
        "schedulingcx.whySchedulingcx.items.operateStandaloneOrExtension.title"
      ),
      details: t(
        "schedulingcx.whySchedulingcx.items.operateStandaloneOrExtension.details",
        { returnObjects: true }
      ) as string[],
      Icon: LinkIcon,
    },
  ]

  const faqs = [
    {
      q: t("schedulingcx.faq.items.integration.question"),
      a: t("schedulingcx.faq.items.integration.answer"),
      Icon: LinkIcon,
    },
    {
      q: t("schedulingcx.faq.items.security.question"),
      a: t("schedulingcx.faq.items.security.answer"),
      Icon: ShieldCheck,
    },
    {
      q: t("schedulingcx.faq.items.implementation.question"),
      a: t("schedulingcx.faq.items.implementation.answer"),
      Icon: FileCheck,
    },
    {
      q: t("schedulingcx.faq.items.pricing.question"),
      a: t("schedulingcx.faq.items.pricing.answer"),
      Icon: BarChart3,
    },
  ]

  const handleDemoClick = () => {
    navigationStore.openDemoModal()
  }

  return (
    <div className="min-h-screen w-full text-white">
      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 inset-x-0 z-50 bg-white/90 backdrop-blur border-t border-slate-200 p-3 sm:hidden">
        <div className="flex gap-3">
          <button
            onClick={handleDemoClick}
            className="flex-1 text-center bg-[#395091] text-white font-semibold px-4 py-3 rounded-lg shadow-md hover:bg-[#2d3f73] transition"
          >
            {t("common.bookDemo")}
          </button>
          <a
            href="#case-study"
            className="flex-1 text-center bg-white text-[#395091] font-semibold px-4 py-3 rounded-lg border border-[#395091]/30 hover:bg-gray-50 transition"
          >
            {t("schedulingcx.mobileCta.caseStudy")}
          </a>
        </div>
      </div>

      {/* HERO + BODY COMBINED */}
      <main className="bg-white text-slate-800">
        <header
          className="w-full"
          style={{
            background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)",
          }}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-white">
            {/* Product Wordmark */}
            <div className="mb-8 sm:mb-10">
              <h2 className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight">
                <span >scheduling</span>
                <span className="font-bold text-[#7AC943]">CX</span>
                <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">
                  ™
                </sup>
              </h2>
            </div>

            {/* Headline & Subcopy */}
            <div className="max-w-3xl">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
                {t("schedulingcx.hero.title")}
              </h1>
              <p className="text-base sm:text-lg md:text-xl/8 opacity-95">
                {t("schedulingcx.hero.subtitle")}
              </p>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={handleDemoClick}
                  className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
                >
                  {t("common.bookDemo")}
                </button>
                <a
                  href="#case-study"
                  className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
                >
                  {t("common.downloadCaseStudy")}
                </a>
              </div>
            </div>
          </div>
        </header>

        {/* Features Section with Value-Driven Heading */}
        <section
          id="features"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            {t("schedulingcx.features.title")}
          </h3>
          <p className="text-slate-600 mb-6">
            {t("schedulingcx.features.subtitle")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {features.map(({ title, body, Icon }, i) => (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm hover:shadow-md transition bg-white/70 backdrop-blur-[1px]"
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#F0F5FF]">
                    <Icon
                      className="w-5 h-5 text-[#395091]"
                      aria-hidden="true"
                    />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">
                    {title}
                  </h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section
          id="use-cases"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 bg-gradient-to-br from-[#EDF7FF] to-[#F9FBFF] rounded-3xl"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 sm:mb-8">
            {t("schedulingcx.useCases.title")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {useCases.map(({ title, body, Icon }, i) => (
              <div
                key={i}
                className="rounded-2xl bg-gradient-to-tr from-[#F0F5FF] to-white border border-slate-200 p-5 sm:p-6 shadow-sm"
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E3ECFF]">
                    <Icon
                      className="w-5 h-5 text-[#395091]"
                      aria-hidden="true"
                    />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">
                    {title}
                  </h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Case Study + Why */}
        <section
          id="case-study"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 scroll-mt-24 lg:scroll-mt-28"
        >
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-start">
            {/* Case Study */}
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 sm:mb-4">
                {t("schedulingcx.caseStudy.title")}
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                {t("schedulingcx.caseStudy.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <a
                  href="/use-case-finder?Org=DFCI"
                  className="inline-block rounded-lg bg-[#395091] text-white px-5 py-3 font-semibold hover:opacity-90 text-center"
                >
                  {t("common.viewCaseStudy")}
                </a>
                <a
                  href="/use-case-finder"
                  className="inline-block text-[#395091] font-semibold hover:underline text-center"
                >
                  {t("schedulingcx.caseStudy.seeMoreStories")}
                </a>
              </div>
              {/* Client logos row */}
              <CustomerLogos logoUids={["rady", "stlukes", "nhs"]} />
            </div>

            {/* Why SchedulingCX */}
            <div className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm bg-gradient-to-br from-[#EDF2FF] via-white to-[#F9FAFB]">
              <h4 className="font-bold text-slate-900 mb-3 text-3xl md:text-4xl">
                {t("schedulingcx.whySchedulingcx.title")}
              </h4>
              <p className="text-slate-700 text-base leading-relaxed mb-4">
                {t("schedulingcx.whySchedulingcx.description")}
              </p>
              <ul className="space-y-3 text-base">
                {whyBullets.map(({ title, details, Icon }, idx) => (
                  <li key={idx}>
                    <details className="group">
                      <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-2">
                        <Icon className="w-5 h-5 text-[#395091]" /> {title}
                      </summary>
                      <div className="mt-2 text-slate-600">
                        <ul className="list-disc ml-5 space-y-1">
                          {details.map((d, i) => (
                            <li key={i}>{d}</li>
                          ))}
                        </ul>
                      </div>
                    </details>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Trust & Compliance */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            {t("schedulingcx.trust.title")}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("schedulingcx.trust.hipaa")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <FileCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("schedulingcx.trust.soc2")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Lock className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("schedulingcx.trust.sso")}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">
                {t("schedulingcx.trust.gdpr")}
              </span>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            {t("schedulingcx.faq.title")}
          </h3>
          <ul className="space-y-4">
            {faqs.map(({ q, a, Icon }, i) => (
              <li key={i} className="border border-slate-200 rounded-lg p-4">
                <details className="group">
                  <summary className="flex items-center gap-2 font-medium cursor-pointer text-slate-900">
                    <Icon className="w-5 h-5 text-[#395091]" /> {q}
                  </summary>
                  <p className="mt-2 text-slate-600 leading-relaxed">{a}</p>
                </details>
              </li>
            ))}
          </ul>
        </section>

        {/* Final CTA */}
        <section id="contact" className="relative mt-6 sm:mt-8">
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)",
            }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
                  {t("schedulingcx.cta.title")}
                </h3>
                <p className="opacity-90 text-base sm:text-lg">
                  {t("schedulingcx.cta.subtitle")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
                <button
                  onClick={handleDemoClick}
                  className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
                >
                  {t("common.bookDemo")}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer note */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 text-slate-500 text-sm">
          {t("schedulingcx.footer")}
        </section>
      </main>
    </div>
  )
}

export default SchedulingCX
